import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { InnView } from "../components/InnView";
import { LocationCardList } from "../components/LocationCardList";
import { NpcView } from "../components/NpcView";
import { ShopView } from "../components/ShopView";
import { ArenaPage } from "./ArenaPage";
import { CookPage } from "./CookPage";
import { ForagePage } from "./ForagePage";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";

// The bracket node's pathname. Hardcoded because the arena floor is
// the only world page the redirect-during-run logic needs to know
// about, and the world-tree's pathname for it is fixed in
// `world/world.yaml`.
const ARENA_FLOOR_PATH = "/world/antar/arena/floor";

// Generic /world/* renderer. Reads the current pathname, fetches the
// matching world-tree node, dispatches by kind. Replaces the old
// per-page WorldPage / AntarPage / MarketsPage components — the tree
// drives the routing now.
export function WorldRoute() {
  const theme = pixelTheme;
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<api.User | null>(null);
  const [node, setNode] = useState<api.WorldNode | null>(null);
  const [arena, setArena] = useState<api.ArenaState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setNode(null);

    void (async () => {
      try {
        const [me, world, arenaResp] = await Promise.all([
          api.getMe(),
          api.getWorldNode(location.pathname),
          api.getArenaState().catch(() => ({ arena: null as api.ArenaState | null })),
        ]);
        if (cancelled) return;
        setUser(me.user);
        setNode(world.node);
        setArena(arenaResp.arena);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load page.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // While an arena run is active, every world page that isn't the
  // bracket itself redirects to the bracket. The clerk's NPC page is
  // unreachable during a run by spec — the player must finish or
  // abandon to talk to Thomas Anton again.
  useEffect(() => {
    if (!arena?.run) return;
    if (location.pathname === ARENA_FLOOR_PATH) return;
    navigate(ARENA_FLOOR_PATH, { replace: true });
  }, [arena?.run?.id, location.pathname, navigate]);

  // Back button climbs one segment up the URL
  // (e.g. `/world/antar/markets/cardmaster` → `/world/antar/markets`).
  // `/world` itself shows no back button — the header logo is the
  // way back to the lobby.
  const atWorldRoot = location.pathname === "/world";
  const parentPath =
    location.pathname.replace(/\/[^/]+$/, "") || "/world";

  const backLabel = parentPath === "/world" ? "◀ WORLD" : "◀ BACK";

  // Forage pages own their own layout (wider game area, custom side
  // panel) so they bypass the standard WorldRoute chrome.
  if (node && node.kind === "forage") {
    return (
      <ForagePage
        kind={node.forage_kind}
        title={node.title}
        breadcrumb={node.breadcrumb ?? node.title}
        parentPath={parentPath}
      />
    );
  }

  // Cooking pages, same model as forage — own layout, own channel.
  if (node && node.kind === "cook") {
    return (
      <CookPage
        title={node.title}
        breadcrumb={node.breadcrumb ?? node.title}
        parentPath={parentPath}
      />
    );
  }

  // Arena bracket. Owns its own layout. Reachable directly via the
  // /world/antar/arena/floor node; while a run is active it's the
  // sole reachable world page (every other path redirects here).
  if (node && node.kind === "arena") {
    return (
      <ArenaPage
        title={node.title}
        breadcrumb={node.breadcrumb ?? node.title}
        parentPath={parentPath}
      />
    );
  }

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto", display: "grid", gap: 16 }}>
        <AppHeader
          user={user}
          backTo={atWorldRoot ? undefined : parentPath}
          backLabel={backLabel}
        />
        <Breadcrumb text={node?.breadcrumb ?? node?.title} />

        {error && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${E32.scarlet}`,
              color: E32.scarlet,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
            }}
          >
            {error}
          </div>
        )}

        {node && node.kind === "nav" && node.id === "inn" && user && (
          <InnView
            node={node}
            sleep={user.sleep}
            onSleepChange={(sleep) => setUser({ ...user, sleep })}
          />
        )}
        {node && node.kind === "nav" && node.id !== "inn" && <NavView node={node} />}
        {node && node.kind === "shop" && (
          <ShopView
            shopId={node.shop.shop_id}
            npc={node.shop.npc}
            onPurchase={(r) => {
              // Refresh user-facing currency / pack count in the
              // header. Cheap full reload of /api/me; the alternative
              // would be threading deltas through props.
              if (user) {
                setUser({
                  ...user,
                  void_juice_ml: r.balances.void_juice_ml,
                  gold_coins: r.balances.gold_coins,
                });
              }
            }}
          />
        )}
        {node && node.kind === "npc" && <NpcView npcId={node.npc_ref} />}
      </div>
    </div>
  );
}

function NavView({
  node,
}: {
  node: Extract<api.WorldNode, { kind: "nav" }>;
}) {
  const theme = pixelTheme;
  return (
    <>
      {node.description && (
        <div
          style={{
            fontSize: 16,
            lineHeight: 1.5,
            color: theme.ink,
            whiteSpace: "pre-wrap",
          }}
        >
          {node.description.trim()}
        </div>
      )}
      {node.children.length > 0 && (
        <LocationCardList
          locations={node.children.map((c) => ({
            id: c.id,
            name: c.title,
            description: c.description ?? "",
            buttonLabel: c.button_label ?? `Go to ${c.title}`,
            to: c.path,
            lockedHint: c.gate ? c.gate.hint : null,
            npcRef: c.npc_ref ?? null,
            defaultMood: c.default_mood ?? null,
          }))}
        />
      )}
    </>
  );
}

